import { useCallback, useEffect } from "react";
import { useBlocker } from "react-router";
import { useDialog } from "contexts/DialogContext";
import { useTranslation } from "react-i18next";

const useUnsavedChanges = (isDirty) => {
  const { t } = useTranslation();
  const { showConfirm, hideConfirm } = useDialog();

  const blocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }) => {
        return isDirty && currentLocation.pathname !== nextLocation.pathname;
      },
      [isDirty],
    ),
  );

  useEffect(() => {
    if (!isDirty) return;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isDirty]);

  const handleConfirm = useCallback(async () => {
    const { location, proceed, reset } = blocker;
    if (!!location) {
      try {
        if (!!location.state?.beforeNavigate) {
          const result = await location.state?.beforeNavigate();
          if (result.error) {
            reset();
            return;
          }
          delete location.state.beforeNavigate;
        }
        proceed();
      } catch (error) {
        reset();
      }
    }
  }, [blocker]);

  const showConfirmMessage = useCallback(
    (handleConfirm) => {
      showConfirm(
        t("youHaveUnsavedChangesAre"),
        "",
        handleConfirm,
        () => blocker?.reset?.(),
      );
    },
    [t, blocker, showConfirm],
  );

  useEffect(() => {
    if (blocker.state !== "blocked" || !isDirty) {
      if (blocker.state === "blocked") {
        // Not unsaved any more (e.g. a click while the loaded values were
        // being set): go on, and close the question shown for this navigation
        hideConfirm?.();
        handleConfirm();
      }
      return;
    }

    showConfirmMessage(handleConfirm);

    // showConfirm(
    //   "You have unsaved changes. Are you sure you want to leave?",
    //   "",
  }, [blocker, handleConfirm, hideConfirm, isDirty, showConfirmMessage]);

  return { blocker, showConfirmMessage };
};

export default useUnsavedChanges;
